import { OrgTestCountAnalytics, PeriodStats, TimePeriod } from "types/analyticsTypes";
import {
  EyeType,
  FDTLabel,
  TestType,
  EyeCoverAction,
  ContinuousPerformanceStageEvent,
  CPTConfigMode,
  DyscalculiaConfigMode,
  AvailableOption,
  AdjustableConfigurationValue,
  ConfigurationChoice,
  TestCategory,
  TestLanguage,
  FDTBehaviourType,
  GoldmannSize,
  WaitDuration,
  OptionalVFATests,
  ThresholdLevel,
  TestConfiguration,
  VFABehaviour,
} from "types/testTypes";
import { ColorVisionPlateNumber } from "types/tests";
import { ALL_TEST_CATEGORIES, TEST_DEFINITIONS } from "@constants/testConstants/testDefinitions";
import {
  ConfigSaveButtonState,
  MantineDataFormat,
  MantineGroupData,
  MantineSelection,
  UIPosition,
  VerticalReferenceLine,
} from "../types/uiTypes";
import { SubmissionType } from "types/userTypes";
import { CONFIGURATION_VALUE_LABEL_KEY_MAP } from "@constants/uiConstants";
import { forEach, isEqual } from "lodash";
import { filterAvailableOption } from "./VersionUtils";
import { HeadsetVersion } from "types/productTypes";
import { extractSavedFields } from "./configurationUtils";

/**
 * Helper function to convert enum value to its corresponding key for CONFIGURATION_VALUE_LABEL_KEY_MAP lookup.
 * For TestCategory and OptionalVFATests enums, returns prefixed key (e.g., "TestCategory.ColorVision", "OptionalVFATests.SWAP").
 * For other enums, returns the enum key (e.g., "TOP" for VFABehaviour.TOP).
 */
export function getEnumKeyFromValue(
  value: AdjustableConfigurationValue | TestCategory,
  configurationFieldName?: string,
): string {
  // Check TestCategory first (needs prefixing to avoid collisions)
  if (Object.values(TestCategory).includes(value as TestCategory)) {
    const key = Object.keys(TestCategory).find(
      (k) => TestCategory[k as keyof typeof TestCategory] === value,
    );
    return key ? `TestCategory.${key}` : String(value);
  }

  // Check OptionalVFATests (needs prefixing to avoid collisions with VFABehaviour)
  if (Object.values(OptionalVFATests).includes(value as OptionalVFATests)) {
    const key = Object.keys(OptionalVFATests).find(
      (k) => OptionalVFATests[k as keyof typeof OptionalVFATests] === value,
    );
    return key ? `OptionalVFATests.${key}` : String(value);
  }

  // Map configuration field names to their corresponding enums
  const fieldToEnumMap: Record<string, { enum: any; name: string }> = {
    testType: { enum: TestType, name: "TestType" },
    eyeType: { enum: EyeType, name: "EyeType" },
    testLanguage: { enum: TestLanguage, name: "TestLanguage" },
    assistantLanguage: { enum: TestLanguage, name: "TestLanguage" },
    behaviourType: { enum: FDTBehaviourType, name: "FDTBehaviourType" },
    fixationSize: { enum: GoldmannSize, name: "GoldmannSize" },
    stimuliSize: { enum: GoldmannSize, name: "GoldmannSize" },
    goldmannSize: { enum: GoldmannSize, name: "GoldmannSize" },
    cptConfigMode: { enum: CPTConfigMode, name: "CPTConfigMode" },
    dyscalculiaConfigMode: { enum: DyscalculiaConfigMode, name: "DyscalculiaConfigMode" },
    testBehaviour: { enum: VFABehaviour, name: "VFABehaviour" },
    testSpeed: { enum: WaitDuration, name: "WaitDuration" },
    speed: { enum: WaitDuration, name: "WaitDuration" },
    dbColor: { enum: ThresholdLevel, name: "ThresholdLevel" },
    plateNumber: { enum: ColorVisionPlateNumber, name: "ColorVisionPlateNumber" },
  };

  // If we have a configuration field name, use it to determine the correct enum
  if (configurationFieldName && fieldToEnumMap[configurationFieldName]) {
    const { enum: enumObj } = fieldToEnumMap[configurationFieldName];
    const key = Object.keys(enumObj).find((k) => enumObj[k as keyof typeof enumObj] === value);
    if (key) return key;
  }

  // Fallback: check all enums in order (for backward compatibility)
  const enums = [
    { enum: TestType, name: "TestType" },
    { enum: EyeType, name: "EyeType" },
    { enum: TestLanguage, name: "TestLanguage" },
    { enum: FDTBehaviourType, name: "FDTBehaviourType" },
    { enum: GoldmannSize, name: "GoldmannSize" },
    { enum: CPTConfigMode, name: "CPTConfigMode" },
    { enum: VFABehaviour, name: "VFABehaviour" },
    { enum: WaitDuration, name: "WaitDuration" },
  ];

  for (const { enum: enumObj } of enums) {
    const key = Object.keys(enumObj).find((k) => enumObj[k as keyof typeof enumObj] === value);
    if (key) return key;
  }

  // If it's a threshold level or other special value, return as-is
  return String(value);
}

export function capitalizeFirstLetter(str: string | undefined): string {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function getFDTLabelNamesForTestType(testType: TestType): FDTLabel[] {
  switch (testType) {
    case TestType.C_20_1:
      return [FDTLabel.P_GTE_1, FDTLabel.P_LT_1, FDTLabel.P_LT_0_5, FDTLabel.MAX];
    case TestType.C_20_5:
      return [FDTLabel.P_GTE_5, FDTLabel.P_LT_5, FDTLabel.P_LT_2, FDTLabel.P_LT_1];
    case TestType.N_30_1:
      return [FDTLabel.P_GTE_1, FDTLabel.P_LT_1, FDTLabel.P_LT_0_5, FDTLabel.MAX];
    case TestType.N_30_5:
      return [FDTLabel.P_GTE_5, FDTLabel.P_LT_5, FDTLabel.P_LT_2, FDTLabel.P_LT_1];
    default:
      throw new Error(`FDT Label Key array not found. Unhandled test type: ${testType}`);
  }
}

export function getFDTLabelImageName(testType: TestType, label: FDTLabel): string {
  switch (testType) {
    case TestType.C_20_1:
      switch (label) {
        case FDTLabel.P_GTE_1:
          return "N";
        case FDTLabel.P_LT_1:
          return "P1";
        case FDTLabel.P_LT_0_5:
          return "P05";
        case FDTLabel.MAX:
          return "P0";
        default:
          throw new Error(`Invalid label: ${label} for test type: ${testType}`);
      }
    case TestType.C_20_5:
      switch (label) {
        case FDTLabel.P_GTE_5:
          return "N";
        case FDTLabel.P_LT_5:
          return "P5";
        case FDTLabel.P_LT_2:
          return "P2";
        case FDTLabel.P_LT_1:
          return "P1";
        default:
          throw new Error(`Invalid label: ${label} for test type: ${testType}`);
      }
    case TestType.N_30_1:
      switch (label) {
        case FDTLabel.P_GTE_1:
          return "N";
        case FDTLabel.P_LT_1:
          return "P1";
        case FDTLabel.P_LT_0_5:
          return "P05";
        case FDTLabel.MAX:
          return "P0";
        default:
          throw new Error(`Invalid label: ${label} for test type: ${testType}`);
      }
    case TestType.N_30_5:
      switch (label) {
        case FDTLabel.P_GTE_5:
          return "N";
        case FDTLabel.P_LT_5:
          return "P5";
        case FDTLabel.P_LT_2:
          return "P2";
        case FDTLabel.P_LT_1:
          return "P1";
        default:
          throw new Error(`Invalid label: ${label} for test type: ${testType}`);
      }
    default:
  }
}

export function aggregateOrgTestCounts(
  analytics: OrgTestCountAnalytics[],
  period: TimePeriod,
): PeriodStats {
  const count = analytics.reduce((sum, item) => sum + (item[period]?.count ?? 0), 0);
  const previousCount = analytics.reduce(
    (sum, item) => sum + (item[period]?.previousCount ?? 0),
    0,
  );
  const percentageChange =
    previousCount === 0
      ? count > 0
        ? 100
        : 0
      : Math.round(((count - previousCount) / previousCount) * 100 * 100) / 100;

  return { count, previousCount, percentageChange };
}

export function getAvailableTestCategories(
  testCategories: string[],
  productVersion: HeadsetVersion,
): AvailableOption<TestCategory> {
  if (!testCategories || testCategories.length === 0) {
    return ALL_TEST_CATEGORIES;
  }

  const option = filterAvailableOption(ALL_TEST_CATEGORIES, productVersion, testCategories);
  return (option ?? ALL_TEST_CATEGORIES) as AvailableOption<TestCategory>;
}

/**
 * Resolves which test should be selected given the current org scope.
 * If `selectedTest` is still in the scope-filtered category list, it is kept;
 * otherwise the first in-scope test is returned (falling back to the global
 * default when the filtered list is empty).
 */
export function resolveInScopeTest(
  scopeList: string[] | null | undefined,
  productVersion: HeadsetVersion,
  selectedTest: TestCategory | null | undefined,
): { test: TestCategory; isSelectedInScope: boolean } {
  const scopedChoices = getAvailableTestCategories(scopeList ?? [], productVersion).choices ?? [];
  const isSelectedInScope =
    !!selectedTest && scopedChoices.some((choice) => choice.value === selectedTest);
  const test =
    isSelectedInScope && selectedTest
      ? selectedTest
      : (scopedChoices[0]?.value ?? ALL_TEST_CATEGORIES.choices[0].value);
  return { test, isSelectedInScope };
}

export function getSubmissionTypeLabel(submissionType: SubmissionType): string | null {
  switch (submissionType) {
    case SubmissionType.ADD_DEVICE:
      return "submissions.submissionTypeAddNewDevice";
    case SubmissionType.REMOVE_DEVICE:
      return "submissions.submissionTypeRemoveDevice";
    default:
      return null;
  }
}

// Helper function to safely get X and Y coordinates regardless of point format
export const getCoordinates = (point: any): [number, number] => {
  if (!point) return [0, 0];

  // If point is an array [x, y]
  if (Array.isArray(point)) {
    return [
      typeof point[0] === "number" ? point[0] : 0,
      typeof point[1] === "number" ? point[1] : 0,
    ];
  }

  // If point is {x, y} object
  if (typeof point === "object" && point !== null) {
    // Handle object with x, y properties
    if ("x" in point && "y" in point) {
      return [typeof point.x === "number" ? point.x : 0, typeof point.y === "number" ? point.y : 0];
    }

    // Handle PercentagePoint with xPercentage, yPercentage
    if ("xPercentage" in point && "yPercentage" in point) {
      return [
        typeof point.xPercentage === "number" ? point.xPercentage : 0,
        typeof point.yPercentage === "number" ? point.yPercentage : 0,
      ];
    }
  }

  // Fallback for any other formats
  return [0, 0];
};

/**
 * Maps a value from one range to another.
 * @param x - The value to map.
 * @param inMin - The minimum of the input range.
 * @param inMax - The maximum of the input range.
 * @param outMin - The minimum of the output range.
 * @param outMax - The maximum of the output range.
 * @returns The mapped value in the output range.
 */
export function mapRange(
  x: number,
  inMin: number,
  inMax: number,
  outMin: number,
  outMax: number,
): number {
  return ((x - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
}

export function getEyeCoverActionLabelDetails(status: EyeCoverAction | null) {
  switch (status) {
    case EyeCoverAction.CoverLeftEyeRightKeptOpen:
      return { color: "green", label: "extraTests.cover.leftCovered" };
    case EyeCoverAction.CoverRightEyeLeftKeptOpen:
      return { color: "green", label: "extraTests.cover.rightCovered" };
    case EyeCoverAction.OpenLeftEyeRightKeptOpen:
      return { color: "green", label: "extraTests.cover.leftUncovered" };
    case EyeCoverAction.OpenRightEyeLeftKeptOpen:
      return { color: "green", label: "extraTests.cover.rightUncovered" };
    case EyeCoverAction.RightKeptOpenLeftKeptOpen:
      return { color: "green", label: "extraTests.cover.bothEyesOpen" };
  }
}

export function getEyeCoverBadgeDetails(status: EyeCoverAction | null) {
  switch (status) {
    case EyeCoverAction.CoverLeftEyeRightKeptOpen:
      return { color: "green", label: "extraTests.cover.leftCovered" };
    case EyeCoverAction.CoverRightEyeLeftKeptOpen:
      return { color: "green", label: "extraTests.cover.rightCovered" };
    case EyeCoverAction.OpenLeftEyeRightKeptOpen:
    case EyeCoverAction.RightKeptOpenLeftKeptOpen:
    case EyeCoverAction.OpenRightEyeLeftKeptOpen:
    default:
      return { color: "green", label: "extraTests.cover.bothUncovered" };
  }
}

export function getTitleForCoverAction(action: EyeCoverAction) {
  switch (action) {
    case EyeCoverAction.CoverLeftEyeRightKeptOpen:
      return "extraTests.cover.rightGazeAfterLeftCovered";
    case EyeCoverAction.OpenLeftEyeRightKeptOpen:
      return "extraTests.cover.leftGazeAfterLeftUncovered";
    case EyeCoverAction.CoverRightEyeLeftKeptOpen:
      return "extraTests.cover.leftEyePath";
    case EyeCoverAction.OpenRightEyeLeftKeptOpen:
      return "extraTests.cover.rightGazeAfterRightUncovered";
    default:
      return "";
  }
}

export function getCPTStageReferenceLines(
  stageEvents: ContinuousPerformanceStageEvent[],
  shouldSkipFirst?: boolean,
): VerticalReferenceLine[] {
  const events = shouldSkipFirst ? stageEvents.slice(1) : stageEvents;
  return events.map((stage) => ({
    x: stage.startTime,
    label: stage.stageName,
  }));
}

export function convertOptionsToMantineDataFormat(
  options: AvailableOption<Exclude<AdjustableConfigurationValue, boolean> | TestCategory>,
  translate: Function,
): MantineDataFormat {
  if (!options.choices || !Array.isArray(options.choices)) {
    console.warn(
      "convertOptionsToMantineDataFormat: options.choices is null or not an array. This should not have happened. Please investigate.",
      { options },
    );
    return [];
  }
  const choices = options.choices as ConfigurationChoice<
    Exclude<AdjustableConfigurationValue, boolean> | TestCategory
  >[];
  let mantineGroupList: MantineGroupData[] = null;
  let mantineSelectionList: MantineSelection[] = null;
  const seenValues = new Set<string>();
  forEach(choices, (choice) => {
    const value = choice.value;
    if (typeof value === "boolean" || value === null) {
      console.warn(
        "convertOptionsToMantineDataFormat: Encountered invalid choice value. This should not have happened. Please investigate.",
        { choice },
        { options },
      );
      return;
    }
    if (seenValues.has(String(value))) {
      console.warn(
        "convertOptionsToMantineDataFormat: Encountered duplicate choice value. Mantine does not support duplicate option values, dropping the repeat.",
        { choice },
        { options },
      );
      return;
    }
    seenValues.add(String(value));
    const enumKey = getEnumKeyFromValue(value, options.configurationFieldName);
    const labelKey = !isNaN(Number(enumKey))
      ? String(enumKey)
      : CONFIGURATION_VALUE_LABEL_KEY_MAP[enumKey]?.labelKey;
    const directLabel =
      typeof choice.label === "string" && choice.label.trim() ? choice.label : null;
    const dataItem: MantineSelection = {
      value: String(value),
      label: directLabel ?? (labelKey ? translate(labelKey) : String(value)),
      ...(choice.disabled ? { disabled: true } : {}),
    };
    const groupKey = CONFIGURATION_VALUE_LABEL_KEY_MAP[enumKey]?.groupKey;
    if (groupKey) {
      if (!mantineGroupList) {
        mantineGroupList = [];
      }
      let group = mantineGroupList.find((g) => g.group === translate(groupKey));
      if (!group) {
        group = {
          group: translate(groupKey),
          items: [dataItem],
        };
      } else {
        group.items.push(dataItem);
      }
      if (!mantineGroupList.includes(group)) {
        mantineGroupList.push(group);
      } else {
        mantineGroupList = mantineGroupList.map((g) => (g.group === group.group ? group : g));
      }
    } else {
      if (!mantineSelectionList) {
        mantineSelectionList = [dataItem];
      } else {
        mantineSelectionList.push(dataItem);
      }
    }
  });
  if (mantineGroupList && mantineSelectionList) {
    console.warn(
      "convertOptionsToMantineDataFormat: Mixed grouped and ungrouped choices detected. This should not have happened. Please make sure that all the shared options are either grouped or ungrouped.",
    );
    mantineGroupList.forEach((group) => {
      group.items.forEach((item) => {
        mantineSelectionList.push(item);
      });
    });
    return mantineSelectionList;
  }
  if (mantineGroupList) {
    return mantineGroupList;
  } else if (mantineSelectionList) {
    return mantineSelectionList;
  } else {
    console.error(
      "convertOptionsToMantineDataFormat: No valid choices found after processing. This should not have happened. Please investigate.",
    );
    return [];
  }
}

export function retrieveConfigurationValueFromMantineSelection(
  val: string,
  optionsHolder: AvailableOption<Exclude<AdjustableConfigurationValue, boolean> | TestCategory>,
): AdjustableConfigurationValue | TestCategory | null {
  let originalOption:
    | ConfigurationChoice<Exclude<AdjustableConfigurationValue, boolean> | TestCategory>
    | undefined;
  if (Array.isArray(optionsHolder?.choices)) {
    originalOption = optionsHolder.choices.find((choice) => String(choice.value) === val);
  }
  return originalOption ? originalOption.value : null;
}

export function GetSelectionBorderStyle(borderPositions?: UIPosition[]) {
  let borderStyle = {};
  const borderColor = "var(--mantine-color-gray-3)";
  borderPositions?.forEach((position) => {
    switch (position) {
      case "top":
        borderStyle = { ...borderStyle, borderTop: "1.5px solid", borderColor: borderColor };
        break;
      case "bottom":
        borderStyle = { ...borderStyle, borderBottom: "1.5px solid", borderColor: borderColor };
        break;
      case "left":
        borderStyle = { ...borderStyle, borderLeft: "1.5px solid", borderColor: borderColor };
        break;
      case "right":
        borderStyle = { ...borderStyle, borderRight: "1.5px solid", borderColor: borderColor };
        break;
    }
  });
  return borderStyle;
}

export function calculateConfigButtonState(
  currentConfig: Partial<TestConfiguration> | null,
  savedConfig: Partial<TestConfiguration> | null | undefined,
  testName: TestCategory,
): ConfigSaveButtonState {
  const defaultConfig = TEST_DEFINITIONS[testName]?.defaultConfiguration;

  const currentSavedFields = extractSavedFields(currentConfig, testName);
  const defaultSavedFields = extractSavedFields(defaultConfig, testName);

  const hasSavedConfig = !!savedConfig && Object.keys(savedConfig).length > 0;
  const isCurrentDifferentFromSaved = !isEqual(currentSavedFields, savedConfig);

  const isCurrentDifferentFromDefault = !isEqual(currentSavedFields, defaultSavedFields);

  const isLoadVisible = hasSavedConfig && isCurrentDifferentFromSaved;
  const isResetVisible = isCurrentDifferentFromDefault;
  const isSaveVisible =
    isCurrentDifferentFromSaved && (hasSavedConfig || isCurrentDifferentFromDefault);

  return {
    isSaveVisible,
    isLoadVisible,
    isResetVisible,
  };
}
